import { useMemo } from 'react';

/**
 * useStrapiLocalization
 * Returns the correct localized entity for the current language.
 * @param entity The Strapi entity with localizations
 * @param locale The current language code (e.g., 'en', 'fr')
 * @returns The entity in the correct language, or fallback to the default
 */
export function useStrapiLocalization<T extends { locale: string; localizations?: T[] }>(
    entity: T | null | undefined,
    locale: string
): T | null {
    return useMemo(() => {
        if (!entity) return null;
        if (entity.locale === locale) return entity;
        if (entity.localizations && Array.isArray(entity.localizations)) {
            const localized = entity.localizations.find((loc) => loc.locale === locale);
            if (localized) {
                // Merge all media-like fields from original if missing in localization
                const merged: any = { ...entity, ...localized };
                for (const key of Object.keys(entity)) {
                    const originalValue = (entity as any)[key];
                    const localizedValue = (localized as any)[key];
                    // Heuristic: media fields are objects with a 'url' property (and maybe 'mime', 'ext', etc.)
                    if (
                        originalValue &&
                        typeof originalValue === 'object' &&
                        !Array.isArray(originalValue) &&
                        'url' in originalValue &&
                        (
                            !localizedValue ||
                            typeof localizedValue !== 'object' ||
                            !('url' in localizedValue) ||
                            !localizedValue.url
                        )
                    ) {
                        merged[key] = originalValue;
                    }
                }
                return merged;
            }
        }
        return entity; // fallback
    }, [entity, locale]);
}
